import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";

import { formatAudCompact } from "./termPaymentUtils";

/**
 * Asked when a COE fee payment is recorded on a term that already holds money.
 *
 * The first payment on an empty term is unambiguous — it is the money, so it goes for verification
 * without a question. Once something is already there the answer depends on what actually happened:
 * either this is another real instalment the account has not seen, or the money went in through the
 * term schedule already and this is a receipt being filed against the COE fee. Guessing either way
 * would silently double the paid amount or silently lose the record.
 */
export default function CoeFeePaymentChoiceDialog({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  amount,
  mode,
  onModeChange,
  canSelfVerify = false,
  autoVerify = false,
  onAutoVerifyChange,
}) {
  const recordOnly = mode === "RECORD_ONLY";

  return (
    <Dialog open={open} onClose={() => !isSubmitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Record {formatAudCompact(Number(amount) || 0)} towards the COE fee</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This term already has payments recorded, so how this one should be treated is not obvious.
        </Typography>

        <RadioGroup value={mode} onChange={(e) => onModeChange?.(e.target.value)}>
          <FormControlLabel
            value="VERIFY"
            disabled={isSubmitting}
            control={<Radio />}
            label={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Send for verification
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  New money the account has not seen. Counts towards paid and due once verified,
                  like any other term payment.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="RECORD_ONLY"
            disabled={isSubmitting}
            control={<Radio />}
            label={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Record only
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The money is already on the account — it was added through the term schedule.
                  Keeps the COE receipt on file and changes nothing about paid, due or profit and
                  loss.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        {canSelfVerify && !recordOnly && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={autoVerify}
                onChange={(e) => onAutoVerifyChange?.(e.target.checked)}
                size="small"
                disabled={isSubmitting}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Verify immediately — skip routing to Accounts
              </Typography>
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : recordOnly ? "Record payment" : "Save payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
